import React, { useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { apiGet, apiPost } from '../api/client';
import { spacing, fontSize, fontWeight } from '../utils/theme';

/**
 * VerifyEmailBanner — dismissible banner shown to customers whose email is
 * still unverified (customers are soft-gated at signup; verification is
 * forced after 3 purchases — see backend utils/verification.ts). Tapping it
 * emails a fresh code (best-effort; 429 = one already went out recently) and
 * opens the verify screen. Mounted globally in UserShell like GiftBanner.
 * Dismiss is per-session — it returns next app open until they verify.
 */
export default function VerifyEmailBanner() {
  const router = useRouter();
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);

  useFocusEffect(useCallback(() => {
    apiGet<{ role: string; email: string; emailVerified?: boolean }>('/api/auth/profile')
      .then(p => setUnverifiedEmail(p.role === 'user' && p.emailVerified === false ? p.email : null))
      .catch(() => { /* non-critical: banner hidden if fetch fails */ });
  }, []));

  if (dismissed || !unverifiedEmail) return null;

  const handleVerify = async () => {
    if (sending) return;
    setSending(true);
    try {
      await apiPost('/api/auth/resend-code', { email: unverifiedEmail, type: 'verification' });
    } catch { /* 429 = a recent code is already in their inbox — proceed */ }
    setSending(false);
    router.push({ pathname: '/verify-email', params: { email: unverifiedEmail } } as never);
  };

  return (
    <View style={styles.banner}>
      <TouchableOpacity style={styles.main} onPress={handleVerify} activeOpacity={0.85} accessibilityRole="button">
        <View style={styles.iconWrap}>
          <Ionicons name="mail-unread" size={20} color="#ffffff" />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.headline} numberOfLines={1}>Please verify your email</Text>
          <Text style={styles.sub} numberOfLines={1}>
            {sending ? 'Sending code…' : 'Tap to get a code — takes 30 seconds'}
          </Text>
        </View>
        <Ionicons name="chevron-forward" size={18} color="rgba(255,255,255,0.85)" />
      </TouchableOpacity>
      <TouchableOpacity onPress={() => setDismissed(true)} hitSlop={10} style={styles.close} accessibilityLabel="Dismiss verification banner">
        <Ionicons name="close" size={20} color="#ffffff" />
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#8a5a12',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    gap: spacing.sm,
  },
  main: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  iconWrap: {
    width: 34, height: 34, borderRadius: 17,
    backgroundColor: 'rgba(255,255,255,0.18)',
    alignItems: 'center', justifyContent: 'center',
  },
  headline: { color: '#ffffff', fontSize: fontSize.sm, fontWeight: fontWeight.bold },
  sub: { color: 'rgba(255,255,255,0.85)', fontSize: fontSize.xs, marginTop: 1 },
  close: { padding: 2 },
});
